import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import BillingAPI from 'dashboard/api/billing';
import { useBillingAccess } from 'dashboard/composables/useBillingAccess';

const buildKashierReturnPayload = query => {
  const payload = { ...query };
  if (typeof window !== 'undefined' && window.location.search) {
    payload.rawQuery = window.location.search.slice(1);
  }
  return payload;
};

export function useKashierReturn() {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const { loadBillingAccess } = useBillingAccess();

  const clearReturnQuery = () => {
    router.replace({
      name: route.name,
      params: { ...route.params },
      query: {},
    });
  };

  const abandonCheckout = async ({ merchantOrderId, invoiceId }) => {
    if (merchantOrderId) {
      await BillingAPI.abandonCheckout({ merchant_order_id: merchantOrderId });
      return;
    }
    if (invoiceId) {
      await BillingAPI.abandonCheckout({ invoice_id: invoiceId });
    }
  };

  const confirmKashierPayment = async query => {
    try {
      const { data } = await BillingAPI.confirmKashirReturn(
        buildKashierReturnPayload(query)
      );
      const status = data?.status;
      if (
        status === 'paid' ||
        status === 'already_paid' ||
        status === 'ignored'
      ) {
        return { ok: true };
      }
      return { ok: false, confirmFailed: true };
    } catch {
      return { ok: false, confirmFailed: true };
    }
  };

  const handleKashierReturnIfNeeded = async () => {
    const { paymentStatus, merchantOrderId, checkoutCancelled, invoiceId } =
      route.query;
    const cancelled = checkoutCancelled === '1' || checkoutCancelled === 'true';
    const hasPaymentReturn = paymentStatus && merchantOrderId;

    if (!hasPaymentReturn && !(cancelled && invoiceId)) return false;

    let paymentConfirmed = false;

    if (hasPaymentReturn && String(paymentStatus).toUpperCase() === 'SUCCESS') {
      const result = await confirmKashierPayment(route.query);
      paymentConfirmed = result.ok;
      if (result.ok) {
        useAlert(
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_RETURN_SUCCESS')
        );
      } else {
        useAlert(
          t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_RETURN_CONFIRM_ERROR')
        );
      }
    } else {
      try {
        await abandonCheckout({
          merchantOrderId,
          invoiceId: invoiceId || undefined,
        });
      } catch {
        // Best-effort cleanup so trial accounts are not left blocked.
      }
      useAlert(
        cancelled
          ? t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_RETURN_CANCELLED')
          : t('GENERAL_SETTINGS.BILLING_USAGE_PAGE.KASHIR_RETURN_FAILED')
      );
    }

    clearReturnQuery();
    await loadBillingAccess();
    return { handled: true, paymentConfirmed };
  };

  return { handleKashierReturnIfNeeded };
}
